import {Component, forwardRef, Input, Optional, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, FormGroupDirective, NG_VALUE_ACCESSOR} from '@angular/forms';
import {INPUT_CONTROL_USER_INTERACTION_KEYS, Item, Keys, PersistentInputComponent} from '@ideals/types';
import {Expressions, Utils} from '@ideals/utils';

export const CHIPS_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ChipsComponent),
  multi: true
};

@Component({
  selector: 'ideals-chips',
  templateUrl: './chips.component.html',
  styleUrls: ['./chips.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [CHIPS_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChipsComponent extends PersistentInputComponent {
  @Input()
  public formControl: UntypedFormArray;
  @Input()
  public addControl: (value: Item<any>) => UntypedFormControl = this._addControl;

  public set value(value: Array<Item<any>>) {
    this._value = value;
  }

  public get value(): Array<Item<any>> {
    return this._value;
  }

  private _value: Array<Item<any>>;

  constructor(
    @Optional()
    public readonly parentFormGroup: FormGroupDirective,
    private readonly _fb: UntypedFormBuilder
  ) {
    super(parentFormGroup);
  }

  public get currentPlaceholder(): string {
    const value = this.formControl && this.formControl.value;

    return value && value.length ? '' : this.placeholder;
  }

  public onKeydown(event: KeyboardEvent): void {
    const {key} = event;
    const {value} = event.target as HTMLInputElement;

    if (INPUT_CONTROL_USER_INTERACTION_KEYS.includes(key)) {
      event.preventDefault();

      if (!this._hasValue(value)) {
        (<HTMLInputElement>event.target).value = '';
        this._addChips(value);
      }
    } else if (key === Keys.Backspace && !value) {
      event.preventDefault();

      this._removeLastChip();
    }
  }

  public onBlur(event: FocusEvent): void {
    const {value} = event.target as HTMLInputElement;
    if (!this._hasValue(value)) {
      (<HTMLInputElement>event.target).value = '';
      this._addChips(value);
    }
  }

  public paste(event: ClipboardEvent): void {
    event.preventDefault();

    const value = event.clipboardData.getData('text')
      .trim();
    if (!this._hasValue(value)) {
      this._addChips(value);
    }
  }

  public onEdit(): void {
    this.doChange(this.formControl.value);
  }

  public onRemove(index: number): void {
    this._remove(index);
  }

  private _addChips(value: string): void {
    if (Expressions.haveNotWhiteCharacters(value)) {
      value.split(Expressions.byCommaOrSemicolon)
        .filter((item) => item && !this._hasValue(item))
        .forEach((item) => this.formControl.push(this.addControl(this._createChip(item))));
      this.doChange(this.formControl.value);
    }
  }

  private _addControl(control: Item<any>): UntypedFormControl {
    return this._fb.control(control);
  }

  private _createChip(userValue: string): Item<any> {
    return new Item<any>({
      id: Utils.getUniqueId,
      caption: userValue
    });
  }

  private _removeLastChip(): void {
    if (this.formControl.controls.length) {
      this._remove(this.formControl.controls.length - 1);
    }
  }

  private _hasValue(userValue: string): boolean {
    const value = userValue.toLowerCase();

    return !!(this.formControl.value && this.formControl.value.some((item) => item.caption.toLowerCase() === value));
  }

  private _remove(index: number): void {
    this.formControl.removeAt(index);

    this.doChange(this.formControl.value);
  }
}
